import { z } from "zod";
import { isAddress } from "viem";

// Helper function to validate Ethereum addresses
const validateAddress = (value: string) => isAddress(value);

// Constants matching the contract
const MAX_TAX = 25;
const MAX_TOTAL_TAX = 100;
const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 32;
const MIN_SYMBOL_LENGTH = 2;
const MAX_SYMBOL_LENGTH = 8;
const MAX_PERCENT = 100;
const MAX_BASIS_POINTS = 10000; // 100% * 100

// Helper function to validate basis points
const validateBasisPoints = (value: string) => {
  const num = parseInt(value);
  return !isNaN(num) && num >= 0 && num <= MAX_BASIS_POINTS;
};

// Valid BNB amounts for liquidity
const validBnbAmounts = ["0", "0.1", "0.5", "1", "2", "5", "10", "25", "50", "100", "custom"];

const initialLiquiditySchema = z.object({
  bnbAmount: z.string(),
  customAmount: z.string(),
  tokenAmount: z.string(),
  customTokenAmount: z.string(),
});

// Helper function to validate percentage
const validatePercentage = (value: string) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0 && num <= MAX_PERCENT;
};

// Helper function to validate tax
const validateTax = (value: string) => {
  if (value === "custom") return true;
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0 && num <= MAX_TAX;
};

// Helper function to validate custom tax
const validateCustomTax = (value: string) => {
  if (value === "") return true;
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0 && num <= MAX_TAX;
};

// Helper function to validate tax distribution value
const validateTaxDistribution = (value: string) => {
  if (value === "custom") return true;
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

export const formSchema = z.object({
  // Basic Token Information
  name: z.string()
    .min(1, "Name is required")
    .max(32, "Name cannot exceed 32 characters"),
  
  symbol: z.string()
    .min(1, "Symbol is required")
    .max(8, "Symbol cannot exceed 8 characters"),
  
  decimals: z.string().default("18"),
  
  totalSupply: z.string()
    .min(1, "Total supply is required")
    .refine(
      (value) => {
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue > 0;
      },
      "Total supply must be a positive number"
    ),
  
  // Trading Settings
  tradingEnabled: z.boolean().default(true),

  // Tax Settings
  buyTax: z.string()
    .refine(
      validateTax,
      `Buy tax must be between 0 and ${MAX_TAX}% or "custom"`
    )
    .default("0"), // Default 0%

  customBuyTax: z.string()
    .refine(
      validateCustomTax,
      `Custom buy tax must be between 0 and ${MAX_TAX}%`
    )
    .default(""),
      
  sellTax: z.string()
    .refine(
      validateTax,
      `Sell tax must be between 0 and ${MAX_TAX}% or "custom"`
    )
    .default("0"), // Default 0%

  customSellTax: z.string()
    .refine(
      validateCustomTax,
      `Custom sell tax must be between 0 and ${MAX_TAX}%`
    )
    .default(""),
  
  // Tax Distribution
  taxDistribution: z.object({
    reflection: z.string()
      .refine(validateTaxDistribution, "Reflection must be 'custom' or between 0 and 100%")
      .default("0"),
    autoLiquidity: z.string()
      .refine(validateTaxDistribution, "Auto-liquidity must be 'custom' or between 0 and 100%")
      .default("0"),
    burn: z.string()
      .refine(validateTaxDistribution, "Burn must be 'custom' or between 0 and 100%")
      .default("0"),
    marketing: z.string()
      .refine(validateTaxDistribution, "Marketing must be 'custom' or between 0 and 100%")
      .default("0"),
    development: z.string()
      .refine(validateTaxDistribution, "Development must be 'custom' or between 0 and 100%")
      .default("0"),
    marketingWallets: z.array(z.string()).default([]),
    developmentWallets: z.array(z.string()).default([])
  }).default({}),

  // Wallet Settings
  marketingWallets: z.array(z.string()).default([]),
  developmentWallets: z.array(z.string()).default([]),
  
  // Security Features - Now using basis points
  maxWalletPercentage: z.string()
    .refine(
      validateBasisPoints,
      "Max wallet percentage must be between 0 and 10000 basis points (0-100%)"
    )
    .default("0"), // No limit

  antiDump: z.object({
    maxBuyPercentage: z.string()
      .refine(
        validateBasisPoints,
        "Max buy percentage must be between 0 and 10000 basis points (0-100%)"
      )
      .default("0"), // No limit

    maxSellPercentage: z.string()
      .refine(
        validateBasisPoints,
        "Max sell percentage must be between 0 and 10000 basis points (0-100%)"
      )
      .default("0"), // No limit

    enabled: z.boolean().default(false)
  }).default({}),

  tradingDelay: z.string().default("0"), // No delay
  contractLocked: z.boolean().default(false), // Contract lock disabled
  permanentTrading: z.boolean().default(false), // Permanent trading disabled
  // Initial Liquidity
  initialLiquidity: initialLiquiditySchema.default({}),
});

export type FormValues = z.infer<typeof formSchema>;

export const tokenFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(32, "Name must be less than 32 characters"),
  symbol: z.string().min(1, "Symbol is required").max(8, "Symbol must be less than 8 characters"),
  totalSupply: z.string().min(1, "Total supply is required"),
  initialLiquidity: initialLiquiditySchema,
  permanentTrading: z.boolean()
});
